"use client";

import { useContext, useEffect, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuthState } from "react-firebase-hooks/auth";

import { AppStateContext } from "@/components/context/AppContext";
import { auth } from "@/components/context/firebase";
import { useFetchUserDetails } from "@/hooks/useFetchUserDetails";
import CustomLoader from "@/components/custom-loader";
import { captureMonitoringError } from "@/lib/monitoring";

const DISABLE_AUTH = String(process.env.NEXT_PUBLIC_DISABLE_AUTH || "").toLowerCase() === "true";

/** -------------------------
 *  ROUTE DEFINITIONS
 * ------------------------- */

// Marketing/content routes (/, /blog, /features, /recruiters, /pricing,
// /about, /contact, …) stay reachable for signed-in users — the blog is a
// growth surface and internal links must not bounce to the dashboard.
// Authenticated users are only redirected off /auth/* and misrouted
// /dashboard/* paths (role validation).

export default function ContextGate({ children }) {
    const {
        userProfile,
        isUserFetching,
        setUserProfile,
        setIsUserFetching
    } = useContext(AppStateContext);

    const [user, authLoading] = useAuthState(auth);
    const { fetchUserDetails } = useFetchUserDetails();

    const pathname = usePathname();
    const router = useRouter();
    const debugUid = process.env.NEXT_PUBLIC_DEBUG_UID || "debug-local-hr";
    const debugRole = process.env.NEXT_PUBLIC_DEBUG_ROLE || "hr";
    const debugFirstName = process.env.NEXT_PUBLIC_DEBUG_FIRST_NAME || "Debug";
    const debugLastName = process.env.NEXT_PUBLIC_DEBUG_LAST_NAME || "User";
    const isAuthPath =
        pathname?.startsWith("/auth") || pathname?.startsWith("/login");
    // The free tools (/tools, /tools/<slug>) are public and carry no
    // auth-gated content — pages may read `user`/`userProfile` solely to
    // pick a CTA destination, never to gate whether generation renders.
    // They must render for an anonymous visitor even while Firebase auth is
    // still resolving or a signed-in user's profile fetch is pending, rather
    // than sit behind the full-screen loader below. This is the only gate
    // this exemption touches: identity, and every dashboard/auth redirect in
    // the effect above, are unchanged.
    const isFreeToolPath =
        pathname === "/tools" || pathname?.startsWith("/tools/");
    /** -------------------------
     *  PREVENT DOUBLE FETCHING
     * ------------------------- */
    const hasFetchedProfile = useRef(false);

    useEffect(() => {
        if (DISABLE_AUTH) {
            // Phase 3 staging: skip Firebase entirely and try to load the
            // real profile through the Next proxy (which injects X-Dev-Uid).
            // useFetchUserDetails falls back to a minimal stub if the profile
            // row does not exist yet.
            if (!userProfile && !hasFetchedProfile.current) {
                hasFetchedProfile.current = true;
                fetchUserDetails({
                    uid: debugUid,
                    displayName: `${debugFirstName} ${debugLastName}`.trim(),
                    email: process.env.NEXT_PUBLIC_DEBUG_EMAIL || "",
                });
            }
            return;
        }
        if (!user) {
            hasFetchedProfile.current = false;
            setIsUserFetching(false);
            return;
        }

        if (!userProfile && !hasFetchedProfile.current) {
            hasFetchedProfile.current = true;
            fetchUserDetails(user);
        }
    }, [user, userProfile, fetchUserDetails, setUserProfile, setIsUserFetching, debugUid, debugRole, debugFirstName, debugLastName]);

    /** -------------------------
     *  ROUTING / GUARD LOGIC
     * ------------------------- */
    useEffect(() => {
        if (DISABLE_AUTH) {
            if (isUserFetching) return;
            // Phase 3E: onboarding flow removed — hr users go straight to
            // /dashboard/agency from any auth/landing/choice path.
            if (
                pathname.startsWith("/auth") ||
                pathname.startsWith("/login") ||
                pathname === "/" ||
                pathname === "/choice"
            ) {
                router.replace("/dashboard/agency");
            }
            return;
        }
        // The candidate dashboard is retired: its addresses open the viewing
        // page for everyone. Decided here as well as in the router, because
        // the checks below would otherwise send the visitor somewhere else in
        // the same pass.
        if (pathname.startsWith("/dashboard/candidate")) {
            router.replace("/my-interviews");
            return;
        }
        if (authLoading || isUserFetching) return;

        const isAuthRoute = pathname.startsWith("/auth");
        const isDashboard = pathname.startsWith("/dashboard");

        /** -------------------------
         *  NO AUTH USER
         * ------------------------- */
        if (!user) {
            if (isDashboard) {
                router.replace("/");
            }
            return;
        }

        /** -------------------------
         *  AUTH USER BUT PROFILE NOT READY
         * ------------------------- */
        if (user && !userProfile) {
            // Profile fetch is in progress, just finished failing silently
            // (e.g. 5 s withTimeout fired in useFetchUserDetails), or about
            // to retry. Do NOT redirect off the current route — that bounces
            // the user from /dashboard/agency/<subroute> back to /, then to
            // /dashboard/agency, losing their original destination (BUG-030).
            // The loader below covers this state until userProfile arrives.
            return;
        }

        // Phase 3E: onboarding / org-details gate removed — hr users land
        // directly on /dashboard/agency without any recruiter-details step.

        /** -------------------------
         *  AUTH USER ON AUTH ROUTES
         * ------------------------- */
        if (isAuthRoute) {
            redirectToDashboard(userProfile.role, pathname, router);
            return;
        }

        /** -------------------------
         *  DASHBOARD ROLE VALIDATION
         * ------------------------- */
        if (isDashboard) {
            redirectToDashboard(userProfile.role, pathname, router);
        }
    }, [
        user,
        userProfile,
        pathname,
        authLoading,
        isUserFetching,
        router
    ]);

    /** -------------------------
     *  LOADER
     * ------------------------- */
    // Show full-screen loader while Firebase is restoring auth state, or
    // while we have an authenticated user but no profile yet. The second
    // branch covers both "fetch in flight" and "fetch finished but didn't
    // populate userProfile" (e.g. withTimeout fired) — in either case we
    // keep the user on the current route instead of bouncing them through
    // "/" (BUG-030). If userProfile is already set (from cache), render
    // children immediately — layout shows while data loads in the background.
    if (!DISABLE_AUTH && (authLoading || (user && !userProfile)) && !isAuthPath && !isFreeToolPath) {
        return <CustomLoader />;
    }

    return <>{children}</>;
}

/** -------------------------
 *  HELPERS
 * ------------------------- */

function redirectToDashboard(role, pathname, router) {
    if (role === "hr") {
        const allowedRoutes = ["/dashboard/agency", "/dashboard/admin"];

        const isAllowed = allowedRoutes.some(route =>
            pathname.startsWith(route)
        );

        if (!isAllowed) {
            router.replace("/dashboard/agency");
        }
    } else {
        // A candidate account has no dashboard and no sign-in page.
        router.replace("/my-interviews");
    }
}
